/**
 * Links to views: opening one, copying one, and saying what could not be opened.
 *
 *  - <ViewLinks /> (once, in the app shell) keeps the caller's person keys current,
 *    opens a link's view on arrival (`?v=1&x=…` inline, `?v=<id>` saved), and shows
 *    the notice when a view names people outside the caller's access.
 *  - <CopyLinkButton /> (the header) copies a link to the view on screen.
 *  - useOpenSharedView() opens a portable view from code — a review comment.
 *
 * See docs/STATE.md for the whole design, and state/sharedView.ts for the format.
 */
import { useCallback, useEffect, useMemo, useState, useSyncExternalStore } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { IconLink } from '@tabler/icons-react'
import { useAuth } from '@/auth/AuthProvider'
import { useDataset } from '@/data/DatasetProvider'
import { liveState, currentSharers } from '@/ui/urlState'
import { useMediaQuery } from '@/ui/useMediaQuery'
import { buildPeopleKeys, peopleKeys, setPeopleKeys } from './personKeys'
import {
  PF_KEY,
  captureView,
  linkFor,
  readIncoming,
  resolveView,
  viewSearch,
  type ResolvedView,
  type SharedView,
} from './sharedView'
import { loadView, saveView } from './viewsApi'

/* ------------------------------------------------------------------ the notice */

type Notice = { kind: 'refused'; count: number } | { kind: 'missing' } | { kind: 'opening' } | null

let notice: Notice = null
const listeners = new Set<() => void>()
const setNotice = (n: Notice) => {
  notice = n
  for (const l of listeners) l()
}
const subscribe = (l: () => void) => {
  listeners.add(l)
  return () => listeners.delete(l)
}

/** "N people … are outside your access", worded once for the banner and the tests. */
export const refusedText = (n: number) =>
  `${n === 1 ? 'Someone' : `${n} people`} in this view ${n === 1 ? 'is' : 'are'} not in your access, so ${n === 1 ? 'is' : 'are'} left out. Nothing about them is shown.`

/* ------------------------------------------------------------------ opening */

/** Puts a resolved view on screen. A link replaces the entry it arrived on; a comment pushes. */
function useApply() {
  const navigate = useNavigate()
  return useCallback(
    (r: ResolvedView, replace: boolean) => {
      navigate({ pathname: r.path, search: r.search }, { replace, state: r.state })
      setNotice(r.refused > 0 ? { kind: 'refused', count: r.refused } : null)
    },
    [navigate],
  )
}

/**
 * Opens a portable view as this caller sees it (their access, their keys). `refused`:
 * people already left out before it got here (an old comment's names).
 */
export function useOpenSharedView(): (v: SharedView, refused?: number) => ResolvedView {
  const { dataset } = useDataset()
  const apply = useApply()
  return useCallback(
    (v: SharedView, refused = 0) => {
      const r = resolveView(v, peopleKeys(), dataset?.crosstab ?? null)
      r.refused += refused
      apply(r, false)
      return r
    },
    [dataset, apply],
  )
}

export function ViewLinks() {
  const { dataset } = useDataset()
  const { token } = useAuth()
  const location = useLocation()
  const navigate = useNavigate()
  const apply = useApply()

  // Before anything below reads them.
  // Everyone served: paid people, then the terminated-worklist people most of whom are
  // not paid in the period, then the No assignment section's last holders (a person on
  // more than one keeps the first row's key).
  const keys = useMemo(
    () =>
      buildPeopleKeys([
        ...(dataset?.employees ?? []),
        ...(dataset?.terminatedWorklists ?? []).map((r) => ({ employee: r.employee, fccKey: r.fccKeys[0] ?? null })),
        ...(dataset?.noAssignment?.holders ?? []).map((r) => ({ employee: r.employee, fccKey: r.fccKeys[0] ?? null })),
      ]),
    [dataset],
  )
  setPeopleKeys(keys)

  const crosstab = dataset?.crosstab ?? null
  useEffect(() => {
    const incoming = readIncoming(location.pathname, location.search)
    if (!incoming) return
    if (incoming.kind === 'plain') {
      // The URL already holds the whole view; `v` only said so. Stamp the entry (no
      // people) and drop it, so a reload is the app's own state from here on.
      const state = liveState()
      state[PF_KEY] ??= {}
      navigate({ pathname: location.pathname, search: viewSearch(location.search) }, { replace: true, state })
      return
    }
    if (incoming.kind === 'inline') {
      apply(resolveView(incoming.view, keys, crosstab), true)
      return
    }
    let live = true
    setNotice({ kind: 'opening' })
    loadView(token, incoming.id).then(
      (v) => {
        if (!live) return
        if (v) apply(resolveView(v, keys, crosstab), true)
        else {
          navigate({ pathname: location.pathname, search: viewSearch(location.search) }, { replace: true, state: { [PF_KEY]: {} } })
          setNotice({ kind: 'missing' })
        }
      },
      () => live && setNotice({ kind: 'missing' }),
    )
    return () => {
      live = false
    }
    // Once per arrival: the search that carried the link.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search])

  const n = useSyncExternalStore(subscribe, () => notice)
  if (!n) return null
  const text =
    n.kind === 'refused'
      ? refusedText(n.count)
      : n.kind === 'missing'
        ? 'That link’s view could not be found. It may have been mistyped, or saved links may be switched off.'
        : 'Opening the shared view…'
  return (
    <div
      role="status"
      data-view-notice={n.kind}
      style={{
        display: 'flex',
        gap: 12,
        alignItems: 'baseline',
        margin: '8px 0',
        padding: '8px 12px',
        borderRadius: 6,
        border: '1px solid var(--rule-2)',
        background: 'var(--surface-2)',
        fontSize: 'var(--t-table)',
      }}
    >
      <span style={{ flex: 1 }}>{text}</span>
      {n.kind !== 'opening' && (
        <button type="button" className="btn" onClick={() => setNotice(null)}>
          Dismiss
        </button>
      )}
    </div>
  )
}

/* ------------------------------------------------------------------ copying */

/** The view on screen, portable. */
export const captureHere = (): SharedView =>
  captureView(
    { pathname: window.location.pathname, search: window.location.search, state: liveState() },
    peopleKeys(),
    currentSharers(),
  )

export function CopyLinkButton() {
  const { token } = useAuth()
  const wide = useMediaQuery('(min-width: 900px)')
  const [said, setSaid] = useState<string | null>(null)
  const [manual, setManual] = useState<string | null>(null)

  useEffect(() => {
    if (!said) return
    const id = window.setTimeout(() => setSaid(null), 4000)
    return () => window.clearTimeout(id)
  }, [said])

  const copy = async () => {
    setManual(null)
    const { url, saved, long } = await linkFor(captureHere(), window.location.origin, (v) => saveView(token, v))
    try {
      await navigator.clipboard.writeText(url)
      setSaid(long ? 'Link copied — a long one: short links are unavailable right now.' : saved ? 'Short link copied.' : 'Link copied.')
    } catch {
      // No clipboard (an insecure origin, a denied permission): show it to copy by hand.
      setManual(url)
    }
  }

  return (
    <span style={{ position: 'relative', display: 'inline-flex' }}>
      <button
        type="button"
        className="btn"
        data-copy-link
        onClick={() => void copy()}
        title="Copy a link to this view. Whoever opens it sees it within their own access."
        aria-label="Copy link to this view"
        style={{ display: 'inline-flex', alignItems: 'center', gap: 6, ...(wide ? {} : { padding: '5px 7px' }) }}
      >
        <IconLink size={15} stroke={1.6} aria-hidden />
        {wide && 'Copy link'}
      </button>
      {(said || manual) && (
        <span
          role="status"
          style={{
            position: 'absolute',
            top: 'calc(100% + 6px)',
            right: 0,
            zIndex: 40,
            width: manual ? 360 : 'max-content',
            maxWidth: '80vw',
            padding: '6px 10px',
            borderRadius: 6,
            border: '1px solid var(--rule-2)',
            background: 'var(--surface)',
            fontSize: 'var(--t-micro)',
            boxShadow: '0 4px 12px rgba(0,0,0,.12)',
          }}
        >
          {manual ? (
            <>
              Copy this link:
              <input
                readOnly
                value={manual}
                autoFocus
                onFocus={(e) => e.currentTarget.select()}
                onBlur={() => setManual(null)}
                style={{ display: 'block', width: '100%', marginTop: 4, font: 'inherit' }}
              />
            </>
          ) : (
            said
          )}
        </span>
      )}
    </span>
  )
}
